import React, { useState } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CtaImage = {
  url: string;
  wp_id: number;
} | null;

export type CtaEntry = {
  id: string;
  audience: string;
  type: string;
  headline: string;
  body: string;
  button_text: string;
  target_url: string;
  image_light: CtaImage;
  image_light_centered?: CtaImage;
  image_dark: CtaImage;
  funnel_stages: string[];
};

export type CtaRegistry = {
  default_theme: string;
  ctas: CtaEntry[];
};

export const EMPTY_CTA: CtaEntry = {
  id: '',
  audience: 'b2b',
  type: 'visual_banner',
  headline: '',
  body: '',
  button_text: '',
  target_url: '',
  image_light: null,
  image_dark: null,
  funnel_stages: ['mid', 'bottom'],
};

export const AUDIENCE_OPTIONS = ['b2b', 'b2c', 'both'];
export const FUNNEL_OPTIONS = ['top', 'mid', 'bottom'];
export const THEME_OPTIONS = ['dark', 'light'];

// ---------------------------------------------------------------------------
// CTA Form
// ---------------------------------------------------------------------------

export function CtaForm({ cta, onChange, onDelete, isNew }: {
  cta: CtaEntry;
  onChange: (updated: CtaEntry) => void;
  onDelete: () => void;
  isNew?: boolean;
}) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const update = (field: string, value: any) => {
    onChange({ ...cta, [field]: value });
  };

  const toggleFunnel = (stage: string) => {
    const current = cta.funnel_stages || [];
    const next = current.includes(stage)
      ? current.filter(s => s !== stage)
      : [...current, stage];
    onChange({ ...cta, funnel_stages: next });
  };

  const updateImage = (field: 'image_light' | 'image_dark', key: 'url' | 'wp_id', value: string) => {
    const current = cta[field];
    if (key === 'url') {
      if (!value && !current?.wp_id) {
        onChange({ ...cta, [field]: null });
      } else {
        onChange({ ...cta, [field]: { url: value, wp_id: current?.wp_id || 0 } });
      }
    } else {
      const numVal = parseInt(value) || 0;
      if (!numVal && !current?.url) {
        onChange({ ...cta, [field]: null });
      } else {
        onChange({ ...cta, [field]: { url: current?.url || '', wp_id: numVal } });
      }
    }
  };

  return (
    <div className="bg-white border rounded p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">
          {isNew ? 'New CTA' : cta.id}
        </h3>
        {!isNew && (
          <button
            type="button"
            className="text-xs text-red-500 hover:text-red-700"
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">ID</label>
          <input
            type="text"
            value={cta.id}
            onChange={(e) => update('id', e.target.value)}
            placeholder="e.g. demo, b2b-enterprise"
            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Audience</label>
          <select
            value={cta.audience}
            onChange={(e) => update('audience', e.target.value)}
            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            {AUDIENCE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Headline</label>
        <input
          type="text"
          value={cta.headline}
          onChange={(e) => update('headline', e.target.value)}
          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Body</label>
        <textarea
          value={cta.body}
          onChange={(e) => update('body', e.target.value)}
          rows={2}
          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Button Text</label>
          <input
            type="text"
            value={cta.button_text}
            onChange={(e) => update('button_text', e.target.value)}
            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Target URL</label>
          <input
            type="text"
            value={cta.target_url}
            onChange={(e) => update('target_url', e.target.value)}
            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Funnel Stages</label>
        <div className="flex gap-3">
          {FUNNEL_OPTIONS.map(stage => (
            <label key={stage} className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={cta.funnel_stages?.includes(stage) || false}
                onChange={() => toggleFunnel(stage)}
                className="rounded border-gray-300"
              />
              {stage}
            </label>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Image Light</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={cta.image_light?.url || ''}
              onChange={(e) => updateImage('image_light', 'url', e.target.value)}
              placeholder="URL"
              className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            <input
              type="text"
              value={cta.image_light?.wp_id || ''}
              onChange={(e) => updateImage('image_light', 'wp_id', e.target.value)}
              placeholder="WP ID"
              className="w-20 px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Image Dark</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={cta.image_dark?.url || ''}
              onChange={(e) => updateImage('image_dark', 'url', e.target.value)}
              placeholder="URL"
              className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            <input
              type="text"
              value={cta.image_dark?.wp_id || ''}
              onChange={(e) => updateImage('image_dark', 'wp_id', e.target.value)}
              placeholder="WP ID"
              className="w-20 px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-md">
          <span className="text-sm text-red-700">Delete this CTA?</span>
          <button
            className="px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700"
            onClick={() => { onDelete(); setShowDeleteConfirm(false); }}
          >
            Delete
          </button>
          <button
            className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
            onClick={() => setShowDeleteConfirm(false)}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
